/**
 * OGC API Tiles / XYZ Tile helper — replaces on-device MBTiles / GeoPackage tile server.
 *
 * Provides MapLibre GL JS source definitions for raster (XYZ) and vector (MVT) tiles.
 */

import type { RasterSourceSpecification, VectorSourceSpecification } from 'maplibre-gl';
import type { TileEndpoints, VectorTileEndpoints } from './OgcServicesContext';

export class OgcTilesClient {
  constructor(
    private rasterEndpoints: TileEndpoints,
    private vectorEndpoints: VectorTileEndpoints,
  ) {}

  /** Get a MapLibre raster source spec for a given raster tile layer */
  getRasterSource(
    key: keyof TileEndpoints,
    opts?: { tileSize?: number; minzoom?: number; maxzoom?: number },
  ): RasterSourceSpecification {
    return {
      type: 'raster',
      tiles: [this.rasterEndpoints[key]],
      tileSize: opts?.tileSize ?? 256,
      minzoom: opts?.minzoom ?? 0,
      maxzoom: opts?.maxzoom ?? 16,
    };
  }

  /** Get a MapLibre raster-dem source spec (Terrarium encoding) */
  getDemSource(opts?: { tileSize?: number; maxzoom?: number }): {
    type: 'raster-dem';
    tiles: string[];
    tileSize: number;
    maxzoom: number;
    encoding: 'terrarium';
  } {
    return {
      type: 'raster-dem',
      tiles: [this.rasterEndpoints.terrainDem],
      tileSize: opts?.tileSize ?? 256,
      maxzoom: opts?.maxzoom ?? 14,
      encoding: 'terrarium',
    };
  }

  /** Get a MapLibre vector source spec for a given vector tile layer */
  getVectorSource(
    key: keyof VectorTileEndpoints,
    opts?: { minzoom?: number; maxzoom?: number },
  ): VectorSourceSpecification | null {
    const endpoint = this.vectorEndpoints[key];
    if (!endpoint) return null;
    return {
      type: 'vector',
      tiles: [endpoint],
      minzoom: opts?.minzoom ?? 0,
      maxzoom: opts?.maxzoom ?? 14,
    };
  }

  /** All raster chart source specs keyed by chart type */
  getAllChartSources(): Record<string, RasterSourceSpecification> {
    return {
      'vfr-sectional': this.getRasterSource('vfrSectional', { maxzoom: 13 }),
      'tac':           this.getRasterSource('tac', { maxzoom: 14 }),
      'ifr-low':       this.getRasterSource('ifrLow', { maxzoom: 13 }),
      'ifr-high':      this.getRasterSource('ifrHigh', { maxzoom: 11 }),
      'ifr-area':      this.getRasterSource('ifrArea', { maxzoom: 13 }),
      'heli-route':    this.getRasterSource('heliRoute', { maxzoom: 14 }),
    };
  }

  /** All vector tile source specs */
  getAllVectorSources(): Partial<Record<string, VectorSourceSpecification>> {
    const sources: Partial<Record<string, VectorSourceSpecification>> = {};
    const airspaces = this.getVectorSource('airspaces');
    const airports = this.getVectorSource('airports');
    const obstacles = this.getVectorSource('obstacles');
    const navaids = this.getVectorSource('navaids');
    const airways = this.getVectorSource('airways');
    if (airspaces) sources['airspaces-vt'] = airspaces;
    if (airports) sources['airports-vt'] = airports;
    if (obstacles) sources['obstacles-vt'] = obstacles;
    if (navaids) sources['navaids-vt'] = navaids;
    if (airways) sources['airways-vt'] = airways;
    return sources;
  }
}
